import { useState } from 'react';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  XCircle,
  TrendingUp,
  Filter,
  Calendar,
  Download,
  Search,
} from 'lucide-react';

export function ValidationDashboard() {
  const [timeRange, setTimeRange] = useState('7d');
  const [layerFilter, setLayerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  // Color mapping for dynamic Tailwind classes
  const colorClasses: Record<string, { bg: string; text: string; bgGradient: string; bgLight: string; border: string }> = {
    green: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      bgGradient: 'bg-gradient-to-r from-green-50 to-white',
      bgLight: 'bg-green-50',
      border: 'border-green-200'
    },
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      bgGradient: 'bg-gradient-to-r from-blue-50 to-white',
      bgLight: 'bg-blue-50',
      border: 'border-blue-200'
    },
    yellow: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-600',
      bgGradient: 'bg-gradient-to-r from-yellow-50 to-white',
      bgLight: 'bg-yellow-50',
      border: 'border-yellow-200'
    },
  };

  const overallStats = {
    totalValidations: 8453,
    overallPassRate: 94.2,
    avgConfidence: 0.89,
    criticalFailures: 12,
    trend: '+2.3%',
  };

  const validationLayers = [
    {
      id: 1,
      name: 'Technical Quality',
      description: 'Format compliance and character limits',
      passRate: 98.1,
      avgConfidence: 0.95,
      totalChecks: 8453,
      failures: 161,
      status: 'excellent',
      color: 'green',
      icon: Shield,
      recentFailures: [
        { id: 'Q12456', reason: 'Alternative exceeds 125 char limit', time: '2h ago' },
        { id: 'Q12448', reason: 'Context exceeds 800 chars', time: '5h ago' },
      ],
    },
    {
      id: 2,
      name: 'Factual Accuracy',
      description: 'Verification against source context',
      passRate: 96.3,
      avgConfidence: 0.92,
      totalChecks: 8453,
      failures: 313,
      status: 'excellent',
      color: 'green',
      icon: CheckCircle,
      recentFailures: [
        { id: 'Q12455', reason: 'Fact not supported by context', time: '1h ago' },
        { id: 'Q12449', reason: 'Contradicts regulation BC-3040', time: '4h ago' },
      ],
    },
    {
      id: 3,
      name: 'OE Alignment',
      description: 'Alignment with learning objectives',
      passRate: 93.8,
      avgConfidence: 0.88,
      totalChecks: 8453,
      failures: 524,
      status: 'good',
      color: 'blue',
      icon: CheckCircle,
      recentFailures: [
        { id: 'Q12454', reason: 'Tests OE.01.02 instead of OE.01.01', time: '30m ago' },
        { id: 'Q12450', reason: 'Requires knowledge beyond OE scope', time: '3h ago' },
      ],
    },
    {
      id: 4,
      name: 'Difficulty Calibration',
      description: 'Appropriate for target difficulty level',
      passRate: 91.5,
      avgConfidence: 0.85,
      totalChecks: 8453,
      failures: 718,
      status: 'good',
      color: 'blue',
      icon: TrendingUp,
      recentFailures: [
        { id: 'Q12453', reason: 'Too easy for "hard" difficulty', time: '45m ago' },
        { id: 'Q12447', reason: 'Requires advanced knowledge for "easy"', time: '6h ago' },
      ],
    },
    {
      id: 5,
      name: 'Clarity',
      description: 'Clear and unambiguous language',
      passRate: 95.2,
      avgConfidence: 0.91,
      totalChecks: 8453,
      failures: 406,
      status: 'excellent',
      color: 'green',
      icon: CheckCircle,
      recentFailures: [
        { id: 'Q12452', reason: 'Ambiguous stem wording', time: '1.5h ago' },
        { id: 'Q12446', reason: 'Two alternatives could be correct', time: '7h ago' },
      ],
    },
    {
      id: 6,
      name: 'Distractor Quality',
      description: 'Plausible wrong answers',
      passRate: 89.7,
      avgConfidence: 0.82,
      totalChecks: 8453,
      failures: 871,
      status: 'warning',
      color: 'yellow',
      icon: AlertCircle,
      recentFailures: [
        { id: 'Q12451', reason: 'Distractor too obviously wrong', time: '2h ago' },
        { id: 'Q12445', reason: 'Distractors not plausible', time: '8h ago' },
      ],
    },
    {
      id: 7,
      name: 'Ethical Compliance',
      description: 'No discriminatory or inappropriate content',
      passRate: 99.8,
      avgConfidence: 0.98,
      totalChecks: 8453,
      failures: 17,
      status: 'excellent',
      color: 'green',
      icon: Shield,
      recentFailures: [
        { id: 'Q12444', reason: 'Potentially biased language', time: '12h ago' },
      ],
    },
    {
      id: 8,
      name: 'Edge Cases',
      description: 'No ambiguous scenarios or outdated info',
      passRate: 92.4,
      avgConfidence: 0.87,
      totalChecks: 8453,
      failures: 642,
      status: 'good',
      color: 'blue',
      icon: CheckCircle,
      recentFailures: [
        { id: 'Q12443', reason: 'Uses outdated regulation', time: '3h ago' },
        { id: 'Q12442', reason: 'Edge case scenario unclear', time: '9h ago' },
      ],
    },
  ];

  const recentFailedQuestions = [
    {
      id: 'Q12456',
      oeCode: 'OE.01.01',
      exam: 'CPA-10',
      failedLayers: ['Technical Quality'],
      timestamp: '2 hours ago',
      confidence: 0.72,
      status: 'needs-review',
    },
    {
      id: 'Q12455',
      oeCode: 'OE.02.03',
      exam: 'CPA-20',
      failedLayers: ['Factual Accuracy', 'OE Alignment'],
      timestamp: '1 hour ago',
      confidence: 0.68,
      status: 'rejected',
    },
    {
      id: 'Q12454',
      oeCode: 'OE.01.01',
      exam: 'CPA-10',
      failedLayers: ['OE Alignment'],
      timestamp: '30 minutes ago',
      confidence: 0.75,
      status: 'retry-pending',
    },
    {
      id: 'Q12453',
      oeCode: 'OE.03.01',
      exam: 'CEA',
      failedLayers: ['Difficulty Calibration', 'Distractor Quality'],
      timestamp: '45 minutes ago',
      confidence: 0.70,
      status: 'needs-review',
    },
  ];

  // Helper function to filter data by time range
  const filterByTimeRange = (timestamp: string) => {
    const timeMap: Record<string, number> = {
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
    };

    // Parse timestamp (e.g., "2 hours ago", "30 minutes ago", "1 hour ago")
    const match = timestamp.match(/(\d+)\s*(hour|minute|min|h|m)/i);
    if (!match) return true; // If we can't parse, include it

    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    let milliseconds = 0;

    if (unit.startsWith('h')) {
      milliseconds = value * 60 * 60 * 1000;
    } else if (unit.startsWith('m')) {
      milliseconds = value * 60 * 1000;
    }

    return milliseconds <= timeMap[timeRange];
  };

  const filteredLayers = layerFilter === 'all'
    ? validationLayers
    : validationLayers.filter(l => l.status === layerFilter);

  const filteredQuestions = recentFailedQuestions.filter(q => {
    const matchesSearch = searchTerm === '' ||
      q.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.oeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.exam.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTimeRange = filterByTimeRange(q.timestamp);

    return matchesSearch && matchesTimeRange;
  });

  const handleExportReport = () => {
    const csvContent = [
      ['Layer', 'Pass Rate', 'Confidence', 'Failures', 'Status'].join(','),
      ...validationLayers.map(layer =>
        [layer.name, `${layer.passRate}%`, layer.avgConfidence, layer.failures, layer.status].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-report-${timeRange}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Validation Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">
            8-layer quality assurance for generated questions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={handleExportReport}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <Shield className="w-8 h-8 text-green-600" />
            <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">
              {overallStats.trend}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Overall Pass Rate</p>
          <p className="text-3xl font-bold text-gray-900">{overallStats.overallPassRate}%</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <CheckCircle className="w-8 h-8 text-blue-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Total Validations</p>
          <p className="text-3xl font-bold text-gray-900">{overallStats.totalValidations.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <TrendingUp className="w-8 h-8 text-#ea7500 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Avg Confidence</p>
          <p className="text-3xl font-bold text-gray-900">{(overallStats.avgConfidence * 100).toFixed(0)}%</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <AlertCircle className="w-8 h-8 text-yellow-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Needs Review</p>
          <p className="text-3xl font-bold text-gray-900">{(overallStats.totalValidations * (1 - overallStats.overallPassRate / 100)).toFixed(0)}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <XCircle className="w-8 h-8 text-red-600 mb-2" />
          <p className="text-sm text-gray-600 mb-1">Critical Failures</p>
          <p className="text-3xl font-bold text-gray-900">{overallStats.criticalFailures}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => setLayerFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            layerFilter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          All Layers (8)
        </button>
        <button
          onClick={() => setLayerFilter('excellent')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            layerFilter === 'excellent'
              ? 'bg-green-600 text-white'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Excellent (5)
        </button>
        <button
          onClick={() => setLayerFilter('good')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            layerFilter === 'good'
              ? 'bg-blue-600 text-white'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Good (2)
        </button>
        <button
          onClick={() => setLayerFilter('warning')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            layerFilter === 'warning'
              ? 'bg-yellow-600 text-white'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Needs Attention (1)
        </button>
      </div>

      {/* Validation Layers Grid */}
      <div className="grid grid-cols-2 gap-6">
        {filteredLayers.map((layer) => {
          const Icon = layer.icon;
          const colors = colorClasses[layer.color as keyof typeof colorClasses];
          return (
            <div
              key={layer.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
            >
              <div className={`px-6 py-4 border-b border-gray-200 ${colors.bgGradient}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 ${colors.bg} rounded-lg`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{layer.name}</h3>
                      <p className="text-xs text-gray-600">{layer.description}</p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      layer.status === 'excellent'
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : layer.status === 'good'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                    }`}
                  >
                    {layer.status}
                  </span>
                </div>
              </div>

              <div className="p-6">
                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Pass Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{layer.passRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Avg Confidence</p>
                    <p className="text-2xl font-bold text-gray-900">{(layer.avgConfidence * 100).toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Failures</p>
                    <p className="text-2xl font-bold text-red-600">{layer.failures}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Pass: {layer.passRate}%</span>
                    <span>Fail: {(100 - layer.passRate).toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={layer.color === 'green' ? 'h-full bg-green-500' : layer.color === 'blue' ? 'h-full bg-blue-500' : 'h-full bg-yellow-500'}
                      style={{ width: `${layer.passRate}%` }}
                    />
                  </div>
                </div>

                {/* Recent Failures */}
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Recent Failures</p>
                  <div className="space-y-2">
                    {layer.recentFailures.slice(0, 2).map((failure) => (
                      <div
                        key={failure.id}
                        className="flex items-start justify-between p-3 bg-red-50 border border-red-100 rounded-lg text-xs"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-red-900">Q#{failure.id}</p>
                          <p className="text-red-700 mt-0.5">{failure.reason}</p>
                        </div>
                        <span className="text-red-600 ml-2">{failure.time}</span>
                      </div>
                    ))}
                    {layer.failures > 2 && (
                      <button className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                        View all {layer.failures} failures →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Failed Questions */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Recent Failed Validations</h3>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Q# or OE code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                <Filter className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredQuestions.map((question) => (
            <div
              key={question.id}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => setSelectedQuestion(question.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedQuestion(question.id);
                      }}
                      className="text-sm font-semibold text-primary-600 hover:text-primary-700 underline"
                    >
                      Q#{question.id}
                    </button>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      {question.oeCode}
                    </span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      {question.exam}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        question.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : question.status === 'retry-pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {question.status.replace('-', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {question.timestamp}
                    </span>
                    <span>Confidence: {(question.confidence * 100).toFixed(0)}%</span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-2">
                    {question.failedLayers.map((layer) => (
                      <span
                        key={layer}
                        className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded border border-red-200"
                      >
                        ✗ {layer}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700">
                    Review
                  </button>
                  <button className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            View all failed validations →
          </button>
        </div>
      </div>

      {/* Question Detail Modal */}
      {selectedQuestion && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedQuestion(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Question Details: {selectedQuestion}</h3>
              <button
                onClick={() => setSelectedQuestion(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm font-medium text-yellow-900 mb-2">Failed Validation Layers</p>
                <div className="flex flex-wrap gap-2">
                  {filteredQuestions.find(q => q.id === selectedQuestion)?.failedLayers.map((layer) => (
                    <span
                      key={layer}
                      className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded border border-red-200"
                    >
                      {layer}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">OE Code</p>
                  <p className="text-base font-medium text-gray-900">
                    {filteredQuestions.find(q => q.id === selectedQuestion)?.oeCode}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Exam</p>
                  <p className="text-base font-medium text-gray-900">
                    {filteredQuestions.find(q => q.id === selectedQuestion)?.exam}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Confidence</p>
                  <p className="text-base font-medium text-gray-900">
                    {(filteredQuestions.find(q => q.id === selectedQuestion)?.confidence || 0) * 100}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded">
                    {filteredQuestions.find(q => q.id === selectedQuestion)?.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                <button className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  View Full Trace
                </button>
                <button className="flex-1 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
                  Retry Validation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
